import { Grid, Typography } from "@mui/material";
import jwt_decode from "jwt-decode";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SignatureCanvas from "react-signature-canvas";
import { changePassword } from "../../redux/reducers/authSlice";
import { getVetDetailsById } from "../../redux/reducers/clinicSlice";
import {
  getClinicVetsWoLoader,
  updateVetByClinic,
} from "../../redux/reducers/vetSlice";
import { newRoleList } from "../../util/arrayList";
import {
  accessList,
  clinicRolesList,
  salutationList,
  specialtyList,
  typeList,
} from "../../util/dropList";
import {
  patchMandateSession,
  readMandateSession,
} from "../../util/mandateSessionStorage";
import CustomButton from "../CustomButton";
import CustomCheckbox from "../CustomCheckbox";
import CustomModal from "../CustomModal/CustomModal";
import CustomTextField from "../CustomTextField";
import CustomUpload from "../CustomUpload";
import { default as Select } from "../Select/Select";

const requiredFields = ["name", "associatedBranches"]; //"userName", //"role"

const toVetsList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};
const initialValues = {
  type: "Vet",
  salutation: "Mr",
  name: "",
  userName: "",
  speciality: null,
  contactNumber: "",
  conType: [],
  email: "",
  password: "",
  role: null,
  designation: null,
  registrationNo: "",
  image: { file: null, previewUrl: null },
  signature: { file: null, previewUrl: null },
  access: [],
  department: null,
  facility: null,
  associatedBranches: [],
};

const initialError = {
  salutation: false,
  name: false,
  userName: false,
  speciality: false,
  conType: false,
  contactNumber: false,
  email: false,
  password: false,
  role: false,
  designation: false,
  access: false,
  department: false,
  associatedBranches: false,
};

const initialHelp = {
  salutation: "",
  name: "",
  userName: "",
  speciality: "",
  conType: "",
  contactNumber: "",
  email: "",
  password: "",
  role: "",
  designation: "",
  access: "",
  department: "",
  associatedBranches: "",
};

const nameExpan = {
  salutation: "Salutation",
  name: "Name",
  userName: "Username",
  speciality: "Speciality",
  conType: "Consultation Type",
  contactNumber: "Contact Number",
  email: "Email",
  password: "Password",
  role: "role",
  designation: "Designation",
  access: "Access",
  department: "Department",
  associatedBranches: "Associated Branches",
};

const initialPassValues = { oldPass: "", newPass: "", confirmPass: "" };
const initialPassHelps = { oldPass: "", newPass: "", confirmPass: "" };
const initialPassErrors = {
  oldPass: false,
  newPass: false,
  confirmPass: false,
};
const vetAccessRole = { name: "Vet", value: "Vet", apiValue: "VET" };
const superAdminRole = {
  name: "Super Admin",
  value: "Super Admin",
  apiValue: "ADS",
};
const vetAccessRolesList = [vetAccessRole, ...clinicRolesList];

const getRoleApiValue = (roleItem) =>
  roleItem?.apiValue ??
  [...clinicRolesList, vetAccessRole, superAdminRole].find(
    (item) => item?.value === (roleItem?.value ?? roleItem),
  )?.apiValue;

const canUpdateAccessRoles = () => {
  const names = new Set();
  const primary = localStorage.getItem("role");
  if (primary) names.add(primary);
  try {
    const stored = JSON.parse(localStorage.getItem("allRoles") || "[]");
    if (Array.isArray(stored)) {
      stored.forEach((item) => {
        if (typeof item === "string" && item) names.add(item);
      });
    }
  } catch {
    // ignore
  }
  return names.has("ROLE_CLINIC") || names.has("ROLE_CLINICSUPERADMIN");
};

const defObj = {
  facilities: [],
  clinic: {},
  clinicBranches: [],
  clinicRoles: [],
};

const EditProfileModal = ({ open, onClose, vetId }) => {
  const dispatch = useDispatch();
  const signRef = useRef();
  const role = localStorage.getItem("role");
  const [signedFile, setSignedFile] = useState(null);
  const [profileValues, setProfileValues] = useState(initialValues);
  const [profileHelps, setProfileHelps] = useState(initialHelp);
  const [profileError, setProfileError] = useState(initialError);
  const [signModVsble, setSignModVsble] = useState(false);
  const [changePassVsble, setChangePassVsble] = useState(false);
  const [passValues, setPassValues] = useState(initialPassValues);
  const [passHelps, setPassHelps] = useState(initialPassHelps);
  const [passErrors, setPassErrors] = useState(initialPassErrors);
  const [showOldPass, setShowOldPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConPass, setShowConPass] = useState(false);
  const { vet: vetDetails } = useSelector((state) => state?.clinic);
  const user = localStorage.getItem("user");
  const profile = JSON.parse(user);
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { facilities, clinic, clinicBranches, clinicRoles } = useMemo(() => {
    if (!mandateDataRaw) return defObj;
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        facilities: Array.isArray(reqData?.facilities)
          ? reqData?.facilities
          : [],
        clinic: reqData?.clinic ?? {},
        clinicBranches: Array.isArray(reqData?.clinicBranches)
          ? reqData?.clinicBranches
          : [],
        clinicRoles: Array.isArray(reqData?.clinicRoles)
          ? reqData?.clinicRoles
          : [],
      };
    } catch {
      return defObj;
    }
  }, [mandateDataRaw]);

  const branches = useMemo(
    () =>
      (clinicBranches ?? [])
        ?.filter((branch) => branch?.active === "Y")
        ?.map((branch) => ({
          name: branch?.branchName ?? "",
          value: branch?.id ?? null,
        })),
    [clinicBranches],
  );

  const showSuperAdminRole = (clinicBranches ?? []).some(
    (branch) => branch?.id === profile?.id && !branch?.branchNumber,
  );
  const roleListWithSuperAdmin = [...clinicRolesList, superAdminRole];
  const vetRoleListWithSuperAdmin = [...vetAccessRolesList, superAdminRole];
  const otherRoleOptions = showSuperAdminRole
    ? roleListWithSuperAdmin
    : clinicRolesList;
  const vetRoleOptions = showSuperAdminRole
    ? vetRoleListWithSuperAdmin
    : vetAccessRolesList;
  const roleFieldDisabled = !canUpdateAccessRoles();

  const refreshVetsAndPatchMandate = () => {
    try {
      const vetData = readMandateSession()?.dataSharing?.vetData ?? false;
      dispatch(getClinicVetsWoLoader(`?type=all&vetData=${vetData}`)).then(
        (res) => {
          try {
            const payload = res?.payload;
            if (payload == null) return;
            patchMandateSession({
              vets: toVetsList(payload),
            });
          } catch {
            // ignore mandate patch errors
          }
        },
      );
    } catch {
      // ignore vets refresh errors
    }
  };

  const checkRoleDoctor = () => {
    const roleDoctor = vetDetails?.userType?.find(
      (usr) => usr?.name === "ROLE_DOCTOR",
    );
    return Boolean(roleDoctor);
  };

  useEffect(() => {
    if (!open || !vetId) return;
    dispatch(getVetDetailsById(vetId));
  }, [open, vetId, dispatch]);

  useEffect(() => {
    if (!open || !vetDetails) return;
    const resolvedVetId = vetDetails?.doctorId ?? vetDetails?.id;
    if (String(resolvedVetId) !== String(vetId)) return;
    populateProfileValues();
  }, [open, vetDetails, facilities, vetId]);

  const populateProfileValues = () => {
    const roleMap = {
      ROLE_BILLING: "Billing",
      ROLE_CLINICADMIN: "Admin",
      ROLE_DOCTOR: "Vet",
      ROLE_PHARMACY: "Pharmacy Technician",
      ROLE_RECEPTION: "Front Desk",
    };

    const hasDoctor = vetDetails?.userType?.some(
      (userRole) => userRole?.name === "ROLE_DOCTOR",
    );
    const hasClinicAdmin = vetDetails?.userType?.some(
      (userRole) => userRole?.name === "ROLE_CLINICADMIN",
    );

    const updatedUserType =
      hasDoctor && hasClinicAdmin
        ? vetDetails?.userType?.filter(
            (userRole) => userRole?.name !== "ROLE_CLINICADMIN",
          )
        : vetDetails?.userType;

    const accessSelected = updatedUserType?.map((ut) => {
      const roleLabel = roleMap[ut?.name];
      return accessList?.find((al) => al?.value === roleLabel)?.value;
    });
    const clinicRoleMap = {
      ROLE_BILLING: "Billing",
      ROLE_RECEPTION: "Front Desk",
      ROLE_GROOMER: "Groomer",
      ROLE_INVENTORY: "Inventory Manager",
      ROLE_RADIOLOGY: "Lab/Radiology Technician",
      ROLE_PHARMACY: "Pharmacist",
      ROLE_CLINICADMIN: "Practice Manager",
      ROLE_DOCTOR: "Paravet/Nurse",
    };
    const toRoleTokens = (raw) => {
      if (Array.isArray(raw)) {
        return raw
          ?.flatMap((item) => {
            if (item && typeof item === "object") {
              return [item?.value ?? item?.name ?? item?.apiValue];
            }
            return String(item ?? "").split(",");
          })
          ?.map((val) => String(val ?? "").trim())
          ?.filter(Boolean);
      }
      if (typeof raw === "string" && raw?.trim()) {
        return raw
          ?.split(",")
          ?.map((val) => val?.trim())
          ?.filter(Boolean);
      }
      return [];
    };
    const matchFromList = (list, val) =>
      list?.find(
        (rl) => rl?.value === val || rl?.name === val || rl?.apiValue === val,
      );
    const uniqueRolesFromTokens = (list, tokens) => {
      const roles = [];
      tokens?.forEach((val) => {
        const match = matchFromList(list, val);
        if (match && !roles.some((rl) => rl?.value === match?.value)) {
          roles.push(match);
        }
      });
      return roles;
    };
    const roleTokens = toRoleTokens(vetDetails?.role);
    const otherRoles = uniqueRolesFromTokens(roleListWithSuperAdmin, [
      ...roleTokens,
      ...toRoleTokens(vetDetails?.access),
      ...(updatedUserType ?? [])
        ?.map((ut) => clinicRoleMap[ut?.name])
        ?.filter(Boolean),
    ]);
    const designation =
      uniqueRolesFromTokens(newRoleList, [
        ...toRoleTokens(vetDetails?.designation),
        ...roleTokens,
      ])?.[0] ?? null;
    const doctorAccessRoles = uniqueRolesFromTokens(vetRoleListWithSuperAdmin, [
      ...roleTokens.filter((token) => !matchFromList(newRoleList, token)),
      ...toRoleTokens(vetDetails?.access),
      "Vet",
      ...(updatedUserType ?? [])
        ?.map((ut) =>
          ut?.name === "ROLE_DOCTOR" ? "Vet" : clinicRoleMap[ut?.name],
        )
        ?.filter(Boolean),
    ]);
    const lockedDoctorRoles = [
      vetAccessRole,
      ...doctorAccessRoles.filter((rl) => rl?.value !== "Vet"),
    ];
    const facMat = facilities?.find(
      (fl) => fl?.facilityId === vetDetails?.facility,
    );
    const reqFacility =
      vetDetails?.facility && facMat
        ? {
            name: facMat?.name,
            value: facMat?.facilityId ?? vetDetails?.facility,
            id: facMat?.facilityId ?? vetDetails?.facility,
          }
        : null;
    const reqConType = vetDetails?.consulationType?.map((li) => {
      const reqCon = typeList?.find(
        (tl) => tl?.value?.toLowerCase() === li?.toLowerCase(),
      );
      return reqCon?.value;
    });

    let reqBranches = [];
    if (Array.isArray(vetDetails?.associatedBranches)) {
      reqBranches = vetDetails.associatedBranches.map((b) =>
        typeof b === "object" ? b?.id : b,
      );
    } else if (
      typeof vetDetails?.associatedBranches === "string" &&
      vetDetails.associatedBranches.trim() !== ""
    ) {
      reqBranches = vetDetails.associatedBranches.split(",").map(Number);
    }
    if (!vetDetails?.associatedBranches) {
      reqBranches =
        (vetDetails?.createdBy ?? clinic?.id)
          ? [vetDetails?.createdBy ?? clinic?.id]
          : [];
    }

    setProfileValues({
      ...initialValues,
      type: checkRoleDoctor() ? "Vet" : "Other",
      name: vetDetails?.name,
      speciality: Array.isArray(vetDetails?.speciality)
        ? vetDetails?.speciality?.map((li) => ({ name: li, value: li }))
        : vetDetails?.speciality
          ? [vetDetails?.speciality]
          : [],
      conType: reqConType ?? [],
      email: vetDetails?.email,
      role: checkRoleDoctor() ? lockedDoctorRoles : otherRoles,
      designation: checkRoleDoctor() ? designation : null,
      access: accessSelected ?? [],
      salutation: checkRoleDoctor()
        ? null
        : vetDetails?.salutation?.length > 0
          ? { name: vetDetails?.salutation, value: vetDetails?.salutation }
          : null,
      contactNumber: vetDetails?.mobile,
      userName: vetDetails?.userName ?? "",
      registrationNo: vetDetails?.registrationNo ?? "",
      image: { file: null, previewUrl: vetDetails?.image },
      signature: { file: null, previewUrl: vetDetails?.signature },
      department:
        vetDetails?.department && vetDetails?.department !== "null"
          ? {
              name: vetDetails?.department,
              value: vetDetails?.department,
            }
          : null,
      facility: reqFacility,
      userName: vetDetails?.userName,
      associatedBranches: reqBranches,
      schedulable: vetDetails?.schedulable,
    });
    setProfileHelps(initialHelp);
    setProfileError(initialError);
    setSignedFile(null);
  };

  const handleClose = () => {
    setProfileValues(initialValues);
    setProfileHelps(initialHelp);
    setProfileError(initialError);
    setSignedFile(null);
    setSignModVsble(false);
    onCloseChangePassMod();
    onClose();
  };

  const handleChangePassword = () => {
    setPassValues(initialPassValues);
    setPassErrors(initialPassErrors);
    setPassHelps(initialPassHelps);
    setChangePassVsble(true);
  };

  const onCloseChangePassMod = () => {
    setChangePassVsble(false);
    setPassValues(initialPassValues);
    setPassErrors(initialPassErrors);
    setPassHelps(initialPassHelps);
    setShowOldPass(false);
    setShowNewPass(false);
    setShowConPass(false);
  };

  const handleChangePassVal = (e) => {
    const { name, value } = e?.target;
    setPassValues({ ...passValues, [name]: value });
    let err = value?.length >= 8 ? false : value?.length > 0 ? true : true;
    let hlp =
      value?.length >= 8
        ? ""
        : value?.length > 0
          ? "Password must be 8 characters"
          : "This is required field";
    if (name === "newPass") {
      hlp =
        value?.length >= 8
          ? passValues?.oldPass === value
            ? "Old & New passwords cannot be the same"
            : ""
          : value?.length > 0
            ? "Password must be 8 characters"
            : "This is required field";
      err =
        value?.length >= 8
          ? passValues?.oldPass === value
            ? true
            : false
          : value?.length > 0
            ? true
            : true;
    }
    if (name === "confirmPass") {
      hlp =
        passValues?.newPass === value
          ? ""
          : "Confirm password not match with new password";
      err = passValues?.newPass === value ? false : true;
    }
    setPassHelps({ ...passHelps, [name]: hlp });
    setPassErrors({
      ...passErrors,
      [name]: err,
    });
  };

  const onSaveChangePassword = () => {
    let errObj = {};
    let hlpObj = {};
    Object.keys(passValues).forEach((key) => {
      if (key === "oldPass") {
        hlpObj = {
          ...hlpObj,
          [key]:
            passValues?.[key]?.length >= 8
              ? ""
              : passValues?.[key]?.length > 0
                ? "Password must be 8 characters"
                : "This is required field",
        };
        errObj = {
          ...errObj,
          [key]:
            passValues?.[key]?.length >= 8
              ? false
              : passValues?.[key]?.length > 0
                ? true
                : true,
        };
      }
      if (key === "newPass") {
        hlpObj = {
          ...hlpObj,
          [key]:
            passValues?.[key]?.length >= 8
              ? passValues?.oldPass === passValues?.[key]
                ? "Old & New passwords cannot be the same"
                : ""
              : passValues?.[key]?.length > 0
                ? "Password must be 8 characters"
                : "This is required field",
        };
        errObj = {
          ...errObj,
          [key]:
            passValues?.[key]?.length >= 8
              ? passValues?.oldPass === passValues?.[key]
                ? true
                : false
              : passValues?.[key]?.length > 0
                ? true
                : true,
        };
      }
      if (key === "confirmPass") {
        hlpObj = {
          ...hlpObj,
          [key]:
            passValues?.[key]?.length >= 8
              ? passValues?.newPass === passValues?.[key]
                ? ""
                : "Confirm password not match with new password"
              : "This is required field",
        };
        errObj = {
          ...errObj,
          [key]:
            passValues?.[key]?.length >= 8
              ? passValues?.newPass === passValues?.[key]
                ? false
                : true
              : true,
        };
      }
    });
    setPassHelps({ ...passHelps, ...hlpObj });
    setPassErrors({ ...passErrors, ...errObj });

    if (Object.keys(errObj)?.some((val) => errObj?.[val] === true)) return;

    dispatch(
      changePassword({
        oldPassword: passValues?.oldPass,
        newPassword: passValues?.confirmPass,
      }),
    ).then((res) => {
      if (res?.payload) {
        if (res?.payload?.status === 406) {
          setPassHelps({ ...passHelps, oldPass: "Incorrect old password" });
          setPassErrors({ ...passErrors, oldPass: true });
        } else {
          if (res?.payload?.data?.accessToken) {
            const decoded = jwt_decode(res?.payload?.data?.accessToken);
            if (decoded?.id === profile?.id) {
              localStorage.setItem(
                "accessToken",
                res?.payload?.data?.accessToken,
              );
              localStorage.setItem(
                "refreshToken",
                res?.payload?.data?.refreshToken,
              );
            }
          }
          onCloseChangePassMod();
        }
      }
    });
  };

  const isFieldEmpty = (value) => {
    const reqValue =
      typeof value === "string" ? value : (value?.value ?? value);
    return reqValue?.length === 0 || !reqValue;
  };

  const handleChange = (name, value) => {
    const nextValue =
      name === "conType" || name === "associatedBranches"
        ? typeof value === "string"
          ? value.split(",")
          : value
        : name === "name"
          ? value?.charAt(0).toUpperCase() + value?.slice(1)
          : value;

    setProfileValues({
      ...profileValues,
      [name]: nextValue,
    });

    if (name === "schedulable") {
      const conTypeEmpty = isFieldEmpty(profileValues?.conType);
      setProfileError({
        ...profileError,
        conType: !!nextValue && conTypeEmpty,
      });
      setProfileHelps({
        ...profileHelps,
        conType:
          !!nextValue && conTypeEmpty
            ? `${nameExpan?.conType} Required Field`
            : "",
      });
      return;
    }

    const requiredKeys = [
      ...requiredFields,
      ...(checkRoleDoctor() ? ["access", "conType"] : []),
    ];
    if (!requiredKeys.includes(name)) return;

    const isEmpty = isFieldEmpty(nextValue);
    const hasError =
      name === "conType" ? !!profileValues?.schedulable && isEmpty : isEmpty;

    setProfileError({ ...profileError, [name]: hasError });
    setProfileHelps({
      ...profileHelps,
      [name]: hasError
        ? `${nameExpan?.[name] ?? "This Field"} Required Field`
        : "",
    });
  };

  const handleValidate = () => {
    let errObj = {},
      hlpObj = {};
    [
      ...requiredFields,
      ...(checkRoleDoctor() ? ["access", "conType"] : []),
    ].forEach((key) => {
      const isEmpty = isFieldEmpty(profileValues?.[key]);
      const hasError =
        key === "conType" ? !!profileValues?.schedulable && isEmpty : isEmpty;
      if (hasError) {
        errObj = { ...errObj, [key]: true };
        hlpObj = { ...hlpObj, [key]: "This Field is required" };
      }
    });
    setProfileError({ ...profileError, ...errObj });
    setProfileHelps({ ...profileHelps, ...hlpObj });
    return Object.keys(errObj).length > 0;
  };

  const dataURLtoFile = (dataUrl, filename) => {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  };

  const handleUpdate = () => {
    const isNotValid = handleValidate();
    if (isNotValid) return;
    const form = new FormData();
    if (signedFile?.file) {
      form.append("signature", signedFile?.file);
    }
    form.append("name", profileValues.name);
    // form.append("userName", profileValues?.userName ?? "");
    // form.append("email", profileValues.email);
    form.append(
      "salutation",
      checkRoleDoctor() ? "" : (profileValues?.salutation?.value ?? ""),
    );
    if (checkRoleDoctor()) {
      form.append(
        "speciality",
        profileValues.speciality?.map((li) => li?.value),
      );
      form.append("consulationType", profileValues.conType);
    }
    form.append("mobile", profileValues?.contactNumber);
    const selectedRoles = Array.isArray(profileValues?.role)
      ? profileValues.role
      : profileValues?.role
        ? [profileValues.role]
        : [];
    if (checkRoleDoctor()) {
      form.append("designation", profileValues?.designation?.value ?? "");
    } else {
      form.append(
        "designation",
        selectedRoles.map((li) => li?.value ?? li),
      );
    }
    form.append(
      "role",
      selectedRoles.map((li) => li?.value ?? li),
    );
    selectedRoles
      ?.map((li) => getRoleApiValue(li))
      ?.filter(Boolean)
      ?.forEach((ac) => form.append("access", ac));
    if (profileValues?.department?.value) {
      form.append("department", profileValues?.department?.value);
    }
    if (profileValues?.facility?.value) {
      form.append("facility", profileValues?.facility?.value);
    }
    if (
      profileValues?.associatedBranches &&
      profileValues.associatedBranches.length > 0
    ) {
      form.append("associatedBranches", profileValues.associatedBranches);
    }
    form.append("schedulable", profileValues?.schedulable);
    if (profileValues?.registrationNo !== "") {
      form.append("registrationNo", profileValues?.registrationNo ?? "");
    }
    if (profileValues?.image?.file) {
      form.append("image", profileValues?.image?.file);
    }
    dispatch(updateVetByClinic({ vetId, form })).then((res) => {
      if (res?.payload) {
        if (profile?.id === vetId) {
          dispatch(getVetDetailsById(vetId)).then((res2) => {
            if (res2?.payload)
              localStorage.setItem("user", JSON.stringify(res2?.payload));
          });
        }
        refreshVetsAndPatchMandate();
        handleClose();
      }
    });
  };

  const onUploadFile = (e) => {
    const reader = new FileReader();
    const file = e?.target?.files[0];
    reader.onloadend = () => {
      setProfileValues({
        ...profileValues,
        image: { file: e.target.files[0], previewUrl: reader.result },
      });
    };
    reader.readAsDataURL(file);
  };

  const handleMultiSelect = (event, name) => {
    const {
      target: { value },
    } = event;
    let reqValue =
      name === "access"
        ? value?.includes("Admin")
          ? ["Admin"]
          : value
        : value;
    if (name === "role" && checkRoleDoctor()) {
      const selected = Array.isArray(reqValue) ? reqValue : [];
      const others = selected.filter((li) => (li?.value ?? li) !== "Vet");
      reqValue = [vetAccessRole, ...others];
    }
    setProfileValues({
      ...profileValues,
      [name]: reqValue,
    });
    setProfileError({
      ...profileError,
      [name]: reqValue?.length > 0 ? false : true,
    });
    setProfileHelps({
      ...profileHelps,
      [name]: reqValue?.length > 0 ? "" : `${nameExpan?.[name]} Required Field`,
    });
  };

  const getDepartments = () => {
    return clinicRoles
      ?.filter((li) => li?.type === "department")
      ?.map((li) => ({
        name: li?.roleName,
        value: li?.roleName,
      }));
  };

  const onCloseSignMod = () => {
    if (signRef.current && !signRef.current.isEmpty()) {
      const dataURL = signRef.current.toDataURL("image/png");
      const file = dataURLtoFile(dataURL, "vetSignature.png");
      setSignedFile({ file, previewUrl: URL.createObjectURL(file) });
      setSignModVsble(false);
    } else {
      console.warn("Signature pad is empty");
    }
  };

  const isSpecialistVet =
    profileValues?.type === "Vet" &&
    profileValues?.designation?.value === "Specialist";
  const vetFieldWidth = isSpecialistVet ? 6 : 12;

  return (
    <>
      <CustomModal
        open={open}
        onClose={handleClose}
        header="Edit Profile"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh">
          <Grid container spacing={1} className="ph20">
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12} className="mb30">
              <CustomUpload
                uploadText="Profile Picture"
                onUploadFile={onUploadFile}
                value={profileValues?.image?.previewUrl}
                profileImg
                imageHeight={140}
              />
            </Grid>
            {checkRoleDoctor() ? null : (
              <Grid item xs={4} sm={4} md={3} lg={3} xl={3}>
                <div>
                  <Select
                    list={salutationList}
                    value={profileValues?.salutation}
                    handleChange={(e, value) =>
                      handleChange("salutation", value)
                    }
                    name="salutation"
                    label="Salutation *"
                    search
                    error={profileError?.salutation}
                    helperText={profileHelps?.salutation}
                  />
                </div>
              </Grid>
            )}
            <Grid
              item
              xs={checkRoleDoctor() ? 12 : 8}
              sm={checkRoleDoctor() ? 12 : 8}
              md={checkRoleDoctor() ? 12 : 9}
              lg={checkRoleDoctor() ? 12 : 9}
              xl={checkRoleDoctor() ? 12 : 9}
            >
              <div>
                <CustomTextField
                  name="name"
                  fullWidth
                  handleChange={(e) => handleChange("name", e?.target?.value)}
                  required={true}
                  label="Name"
                  value={profileValues?.name}
                  helperText={profileHelps?.name}
                  error={profileError?.name}
                />
              </div>
            </Grid>

            {checkRoleDoctor() ? (
              <>
                <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                  <div className="mt20">
                    <Select
                      list={newRoleList}
                      value={profileValues?.designation}
                      handleChange={(e, value) =>
                        handleChange("designation", value)
                      }
                      label="Designation *"
                      search
                      error={profileError?.designation}
                      helperText={profileHelps?.designation}
                    />
                  </div>
                </Grid>
                <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                  <div className="mt20">
                    <Select
                      list={vetRoleOptions}
                      value={profileValues?.role ?? []}
                      handleChange={(e) => handleMultiSelect(e, "role")}
                      label="Access & Roles"
                      multiSelectSearchCheck
                      disabled={roleFieldDisabled}
                      error={profileError?.role}
                      helperText={profileHelps?.role}
                    />
                  </div>
                </Grid>
              </>
            ) : (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="mt20">
                  <Select
                    list={otherRoleOptions}
                    value={profileValues?.role ?? []}
                    handleChange={(e) => handleMultiSelect(e, "role")}
                    label="Role"
                    multiSelectSearchCheck
                    disabled={roleFieldDisabled}
                    error={profileError?.role}
                    helperText={profileHelps?.role}
                  />
                </div>
              </Grid>
            )}

            {isSpecialistVet ? (
              <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                <div className="mt20">
                  <Select
                    list={specialtyList}
                    value={profileValues?.speciality ?? []}
                    name="speciality"
                    handleChange={(e) =>
                      handleChange("speciality", e?.target?.value)
                    }
                    label="Speciality"
                    error={profileError?.speciality}
                    helperText={profileHelps?.speciality}
                    multiSelectSearchCheck
                  />
                </div>
              </Grid>
            ) : null}
            {profileValues?.type === "Vet" ||
            (Array.isArray(profileValues?.role)
              ? profileValues.role.some(
                  (li) =>
                    (li?.value ?? li) === "Groomer" ||
                    (li?.value ?? li) === "Lab/Radiology Technician",
                )
              : profileValues?.role?.value === "Groomer" ||
                profileValues?.role?.value === "Lab/Radiology Technician") ? (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="">
                  <CustomCheckbox
                    label="Schedulable"
                    checked={profileValues?.schedulable}
                    onChange={() =>
                      handleChange("schedulable", !profileValues?.schedulable)
                    }
                  />
                </div>
              </Grid>
            ) : null}
            {profileValues?.type === "Vet" && profileValues?.schedulable ? (
              <Grid
                item
                xs={vetFieldWidth}
                sm={vetFieldWidth}
                md={vetFieldWidth}
                lg={vetFieldWidth}
                xl={vetFieldWidth}
              >
                <div className="">
                  <Select
                    list={typeList}
                    value={profileValues?.conType}
                    handleChange={(e) =>
                      handleChange("conType", e?.target?.value)
                    }
                    multiSelectTagCheck
                    error={profileError?.conType}
                    helperText={profileHelps?.conType}
                    label="Consultation Type *"
                  />
                </div>
              </Grid>
            ) : null}

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div
                className={
                  profileValues?.schedulable && profileValues?.type === "Vet"
                    ? "mt20"
                    : ""
                }
              >
                <Select
                  list={getDepartments()}
                  value={profileValues?.department}
                  handleChange={(e, value) => handleChange("department", value)}
                  required
                  label="Department"
                  search
                  error={profileError?.department}
                  helperText={profileHelps?.department}
                  disabled={role !== "ROLE_CLINIC"}
                />
              </div>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="mt20">
                <Select
                  list={branches}
                  value={profileValues?.associatedBranches ?? []}
                  handleChange={(e) =>
                    handleChange("associatedBranches", e?.target?.value)
                  }
                  label="Associated Branches"
                  multiSelectTagCheck
                  error={profileError?.associatedBranches}
                  helperText={profileHelps?.associatedBranches}
                />
              </div>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="mt20">
                <CustomTextField
                  name="email"
                  fullWidth
                  handleChange={(e) => handleChange("email", e?.target?.value)}
                  value={profileValues?.email}
                  helperText={profileHelps?.email}
                  error={profileError?.email}
                  label="Email"
                  // required={true}
                  disabled
                />
              </div>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="mt20">
                <CustomTextField
                  name="userName"
                  fullWidth
                  handleChange={(e) =>
                    handleChange("userName", e?.target?.value)
                  }
                  value={profileValues?.userName}
                  helperText={profileHelps?.userName}
                  error={profileError?.userName}
                  label="Username"
                  disabled
                />
              </div>
            </Grid>

            {/* {checkRoleDoctor() ? (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="mt20">
                  <Select
                    list={accessList}
                    value={profileValues?.access}
                    handleChange={(e) => handleMultiSelect(e, "access")}
                    multiSelectTagCheck
                    label="Access *"
                    error={profileError?.access}
                    helperText={profileHelps?.access}
                  />
                </div>
              </Grid>
            ) : null} */}

            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <div className="mt20">
                <CustomTextField
                  value={profileValues?.contactNumber}
                  handleChange={(e) =>
                    handleChange("contactNumber", e?.target?.value)
                  }
                  name="Contact Number"
                  error={profileError?.contactNumber}
                  fullWidth
                  helperText={profileHelps?.contactNumber}
                  label="Phone Number"
                  disabled
                />
              </div>
            </Grid>
            {checkRoleDoctor() ? (
              <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                <div className="mt20">
                  <CustomTextField
                    value={profileValues?.registrationNo}
                    handleChange={(e) =>
                      handleChange("registrationNo", e?.target?.value)
                    }
                    fullWidth
                    label="Registration No."
                  />
                </div>
              </Grid>
            ) : null}
            {checkRoleDoctor() ? (
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <div className="txt-semi-bold fs14 balck mt10">Signature</div>
                {(signedFile?.previewUrl ??
                profileValues?.signature?.previewUrl) ? (
                  <img
                    className="h50"
                    alt=""
                    src={
                      signedFile?.previewUrl ??
                      profileValues?.signature?.previewUrl
                    }
                  />
                ) : null}
                <Typography
                  className="fs14 blue-color font-bold cursor"
                  onClick={() => {
                    setSignModVsble(true);
                  }}
                >
                  {profileValues?.signature?.previewUrl ||
                  signedFile?.previewUrl
                    ? "Update"
                    : "Add"}
                </Typography>
              </Grid>
            ) : null}

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Typography
                className="fs14 blue-color font-bold cursor mt10"
                onClick={handleChangePassword}
              >
                Change Password
              </Typography>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="flex1-end">
                <div className="w20Per mt20">
                  <CustomButton text="Update" onClick={handleUpdate} smallBtn />
                </div>
              </div>
            </Grid>
          </Grid>
        </div>
      </CustomModal>

      <CustomModal
        open={signModVsble}
        onClose={() => setSignModVsble(false)}
        header="Signature"
        modal
        modalWidth={70}
      >
        <SignatureCanvas
          ref={signRef}
          canvasProps={{
            width: (window.innerWidth / 100) * 65,
            height: 300,
            className: "sigCanvas",
          }}
        />
        <div className="flex-center">
          <div>
            <CustomButton text="Save" smallBtn onClick={onCloseSignMod} />
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={changePassVsble}
        onClose={onCloseChangePassMod}
        header="Change Password"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh">
          <div className="mh20">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <CustomTextField
                  label="Old Password"
                  name="oldPass"
                  fullWidth
                  handleChange={handleChangePassVal}
                  value={passValues?.oldPass}
                  error={passErrors?.oldPass}
                  helperText={passHelps?.oldPass}
                  showPassword={showOldPass}
                  handleClickShowPassword={() => setShowOldPass(!showOldPass)}
                  labelTop
                  mandatoryField
                  password
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <CustomTextField
                  label="New Password"
                  name="newPass"
                  fullWidth
                  handleChange={handleChangePassVal}
                  value={passValues?.newPass}
                  error={passErrors?.newPass}
                  helperText={passHelps?.newPass}
                  showPassword={showNewPass}
                  handleClickShowPassword={() => setShowNewPass(!showNewPass)}
                  labelTop
                  mandatoryField
                  password
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                <CustomTextField
                  label="Confirm Password"
                  name="confirmPass"
                  fullWidth
                  handleChange={handleChangePassVal}
                  value={passValues?.confirmPass}
                  error={passErrors?.confirmPass}
                  helperText={passHelps?.confirmPass}
                  showPassword={showConPass}
                  handleClickShowPassword={() => setShowConPass(!showConPass)}
                  labelTop
                  mandatoryField
                  password
                />
              </Grid>
            </Grid>

            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="flex-end mt20">
                <div>
                  <CustomButton
                    text="Save"
                    onClick={onSaveChangePassword}
                    smallBtn
                  />
                </div>
              </div>
            </Grid>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

EditProfileModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  vetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

EditProfileModal.defaultProps = {
  vetId: null,
};

export default EditProfileModal;
